import { QueryClient } from "@tanstack/react-query";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: (failureCount, error) => {
        if (error?.status >= 400 && error?.status < 500) {
          return false;
        }
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: true,
      refetchOnReconnect: false,
    },
    mutations: {
      retry: 1,
      retryDelay: 1000,
    },
  },
});

queryClient.setMutationDefaults(["profile"], {
  mutationFn: async (data) => {
    throw new Error("No mutation function provided");
  },
  onError: (error) => {},
});

queryClient.setQueryDefaults(["profile"], {
  onError: (error) => {
    if (error?.status === 401) {
      window.location.href = "/";
    }
  },
});

export const prefetchProfile = async (userId) => {
  await queryClient.prefetchQuery({
    queryKey: ["profile", userId],
    queryFn: () => {
      return fetch(`/api/profile/${userId}`).then((res) => res.json());
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const cacheUtils = {
  clearProfileCache: () => {
    queryClient.removeQueries({ queryKey: ["profile"] });
  },

  invalidateProfile: (userId) => {
    if (userId) {
      queryClient.invalidateQueries({ queryKey: ["profile", userId] });
    } else {
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    }
  },

  updateProfileCache: (userId, newData) => {
    const queryKey = userId ? ["profile", userId] : ["profile", "me"];
    queryClient.setQueryData(queryKey, (oldData) => ({
      ...oldData,
      ...newData,
    }));
  },

  getCachedProfile: (userId) => {
    const queryKey = userId ? ["profile", userId] : ["profile", "me"];
    return queryClient.getQueryData(queryKey);
  },

  hasProfileInCache: (userId) => {
    const queryKey = userId ? ["profile", userId] : ["profile", "me"];
    return queryClient.getQueryData(queryKey) !== undefined;
  },
};

// export const followUpdateUtils = {
//   updateFollowersCount: (queryClient, username, newFollowerCount) => {
//     queryClient.setQueryData(
//       ["profile", "username", username.replace("@", "")],
//       (oldData) => {
//         if (!oldData) return oldData;
//         return {
//           ...oldData,
//           followers: newFollowerCount,
//         };
//       }
//     );
//   },

//   updateAdmirersCount: (queryClient, username, newAdmirersCount) => {
//     queryClient.setQueryData(
//       ["profile", "username", username.replace("@", "")],
//       (oldData) => {
//         if (!oldData) return oldData;
//         return {
//           ...oldData,
//           admirers: newAdmirersCount,
//         };
//       }
//     );
//   },
// };

export const profileStatusUtils = {
  updateFollowStatus: (profileID, isFollowing, isAccepted, newFollowerCount) => {
    queryClient.setQueriesData(
      { queryKey: ["profile"] },
      (oldData) => {
        if (!oldData || oldData.profileID !== profileID) {
          return oldData;
        }
        return {
          ...oldData,
          followers: newFollowerCount,
          followStatus: {
            isFollowing: isFollowing,
            isAccepted: isAccepted || 0,
          },
        };
      }
    );
  },

  updateAdmireStatus: (profileID, isAdmiring, admireType, newAdmirersCount) => {
    queryClient.setQueriesData(
      { queryKey: ["profile"] },
      (oldData) => {
        if (!oldData || oldData.profileID !== profileID) {
          return oldData;
        }
        return {
          ...oldData,
          admirers: newAdmirersCount,
          admireStatus: isAdmiring
            ? {
                isAdmiring: true,
                admireType: admireType,
              }
            : null,
        };
      }
    );
  },
};


export default queryClient;